import React, { useEffect, useState } from "react";
import { Source, Layer } from "react-map-gl";

type Order = any;

function getCoordinates(order: Order) {
  const storeLat = Number(order?.store?.latitude);
  const storeLng = Number(order?.store?.longitude);

  const deliveryLat = Number(
    order?.food_orders?.latitude || order?.smart_logistics_orders?.latitude
  );
  const deliveryLng = Number(
    order?.food_orders?.longitude || order?.smart_logistics_orders?.longitude
  );

  if (![storeLat, storeLng, deliveryLat, deliveryLng].every(Number.isFinite)) {
    return null;
  }

  return {
    start: [storeLng, storeLat] as [number, number],
    end: [deliveryLng, deliveryLat] as [number, number],
  };
}

async function fetchRoute(start: [number, number], end: [number, number]) {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  if (!token) return null;

  const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${start[0]},${start[1]};${end[0]},${end[1]}?access_token=${token}&geometries=geojson&exclude=ferry`;

  try {
    const res = await fetch(url);
    const data = await res.json();

    if (data.code === "Ok" && data.routes?.[0]?.geometry) {
      const coords = data.routes[0].geometry.coordinates;
      return {
        route: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: data.routes[0].geometry,
              properties: {},
            },
          ],
        },
        points: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: coords[0] }, // start
              properties: { type: "start" },
            },
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: coords[coords.length - 1],
              }, // end
              properties: { type: "end" },
            },
          ],
        },
      };
    }
  } catch (error) {
    console.error("Route fetch failed:", error);
  }

  return null;
}

export default function OrderRoute({ order, color }: any) {
  const [routeData, setRouteData] = useState<any>(null);
  const [pointsData, setPointsData] = useState<any>(null);

  useEffect(() => {
    if (!order) return;

    const coords = getCoordinates(order);
    if (!coords) {
      console.log(`No valid coordinates for order ${order.number || order.id}`);
      return;
    }

    fetchRoute(coords.start, coords.end).then((data) => {
      if (data) {
        setRouteData(data.route);
        setPointsData(data.points);
      }
    });
  }, [order?.id]);

  if (!routeData || !pointsData) return null;

  return (
    <>
      {/* Route line */}
      <Source id={`route-${order.id}`} type="geojson" data={routeData}>
        <Layer
          id={`route-line-${order.id}`}
          type="line"
          paint={{
            "line-color": color,
            "line-width": 3,
          }}
        />
      </Source>

      {/* Start & end markers */}
      <Source id={`route-points-${order.id}`} type="geojson" data={pointsData}>
        <Layer
          id={`route-points-layer-${order.id}`}
          type="circle"
          paint={{
            "circle-radius": 5,
            "circle-color": [
              "match",
              ["get", "type"],
              "start",
              "#fff000", // green start
              "end",
              "#00ff00", // red end
              "#000000",
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          }}
        />
      </Source>
    </>
  );
}
